import * as React from 'react';
import * as styled from './styled';
import {Field, FieldProps, useFormikContext} from 'formik';
import {EstimateType, Point, TaskObject} from 'api/task/types';
import {PointRange} from './components/PointRange';
import {getTotalScore} from 'components/TaskBuilder/hooks/getTotalScore';
import {SettingsGroup} from 'components/SettingsGroup';
import {BuilderValue} from 'components/Builder';
import Row from 'components/Row';
import Column from 'components/Column';
import Radio from 'components/Radio';

/**
 * The estimate form of the task settings.
 * @return {React.ReactElement}
 * @constructor
 */
export default function Estimate(): React.ReactElement {
  const {
    values, setFieldValue,
  } = useFormikContext<Partial<BuilderValue<TaskObject>>>();

  /**
   * Render type.
   * @return {JSX.Element}
   */
  function renderType(): JSX.Element {
    return (
      <Row>
        <Column>
          <styled.Note>
            Выберите систему оценок:
          </styled.Note>
          <styled.Types>
            <Field name="settings.estimate.type">
              {({field}: FieldProps) => (
                <Radio
                  {...field}
                  label="Зачет"
                  checked={field.value === EstimateType.Credit}
                  value={EstimateType.Credit}
                  required
                />
              )}
            </Field>
            <Field name="settings.estimate.type">
              {({field}: FieldProps) => (
                <Radio
                  {...field}
                  label="5-балльная система"
                  checked={field.value === EstimateType.Five}
                  value={EstimateType.Five}
                  required
                />
              )}
            </Field>
            <Field name="settings.estimate.type">
              {({field}: FieldProps) => (
                <Radio
                  {...field}
                  label="10-балльная система"
                  checked={field.value === EstimateType.Ten}
                  value={EstimateType.Ten}
                  required
                />
              )}
            </Field>
          </styled.Types>
        </Column>
      </Row>
    );
  }

  /**
   * Render total score.
   * @return {JSX.Element}
   */
  function renderTotalScore(): JSX.Element {
    return (
      <Row>
        <Column>
          <styled.TotalScore>
            Максимально возможный балл: {getTotalScore()}
          </styled.TotalScore>
          <styled.Note>
            Максимальный балл автоматически суммируется из баллов
            за каждый правильний ответ.
          </styled.Note>
        </Column>
      </Row>
    );
  }

  /**
   * Render points.
   * @return {JSX.Element}
   */
  function renderPoints(): JSX.Element {
    const points = values.settings?.estimate.points;

    /**
     * The change event handler.
     * @param {Point} value
     * @param {number} index
     */
    function handleChange(value: Point, index: number): void {
      setFieldValue(`settings.estimate.points.${index}`, value);
    }

    return (
      <Row>
        <Column>
          {points?.map((point, index) => (
            <PointRange
              key={index}
              length={points?.length}
              index={index}
              value={point}
              points={points}
              onChange={(value) => (
                handleChange(value, index)
              )}
            />
          ))}
        </Column>
      </Row>
    );
  }

  return (
    <SettingsGroup<TaskObject['settings']> title="Оценка задания">
      {renderType()}
      {renderTotalScore()}
      {renderPoints()}
    </SettingsGroup>
  );
}
